<template>
	<div
		class="tr"
		:class="[
			{ disabled: entry.disabled },
			{ 'is-highlight-tool': isHighlightTool },
			{ 'is-temporarily-selected': temporaryHighlight },
			{ 'is-hovered': isHovered }
		]"
		@mouseenter="$emit('row-hover', entry)"
		@mouseleave="$emit('row-hover-end')"
	>
		<div
			v-if="canSelect"
			class="td checkbox-wrapper -col-checkbox"
		>
			<div
				v-if="isHighlightTool"
				class="checked-indicator"
			>
				<Icon
					v-if="checked"
					icon-name="check"
					size="sm"
				/>
			</div>
			<Checkbox
				v-else
				:id="entry.id"
				:value="checked"
				:theme="`dark`"
				:class="{ 'is-visible': isCheckboxVisible }"
				:size="`small`"
				@change="handleSelect"
			/>
		</div>
		<div
			v-else
			class="td spacer"
		/>
		<div
			v-for="col in columns"
			:key="`data-${entry.id}-${col.key}`"
			class="td"
			:class="{
				'text-right': col.key === 'time' || col.key === 'duration',
				'options text-right': col.key === 'options',
			}"
			:style="`width: ${col.size}%`"
			@click="handleRowClick(entry)"
		>
			<span
				v-if="col.key !== 'options'"
				:class="{
					'action-name': col.key === 'action',
				}"
			>
				<slot
					:name="`col-${col.key}`"
					:entry="entry"
					:col="col"
				>
					{{ entry[col.key] }}
				</slot>
			</span>
			<!-- item options cell -->
			<div
				v-else-if="hasContextMenu"
				class="row-actions"
			>
				<slot
					name="row-actions"
					:menu-items="menuItems"
					:entry="entry"
				>
					<ContextMenu
						:key="`context_menu${entry.id}`"
						:menu-items="menuItems"
						:value="entry"
					>
						<template #toggle>
							<span class="sc-white">
								<Icon
									icon-name="context-menu"
									size="md"
								/>
							</span>
						</template>
					</ContextMenu>
				</slot>
			</div>
		</div>

		<slot
			name="row-extra"
			:entry="entry"
		/>
	</div>
</template>

<script>
import Checkbox from '@components/checkbox/Checkbox';
import Icon from '@components/Icon';
import ContextMenu from '@components/context-menu/ContextMenu.vue';

export default {
	name:       'TagRow',
	components: { Checkbox, Icon, ContextMenu },
	props:      {
		entry: {
			type:    Object,
			default: null,
		},
		canSelect: {
			type: Boolean,
		},
		sportName: {
			type:    String,
			default: '',
		},
		columns: {
			type:    Array,
			default: () => [],
		},
		checkboxAlwaysVisible: {
			type:    Boolean,
			default: true,
		},
		checked: {
			type:    Boolean,
			default: false,
		},
		menuItems: {
			type:    Array,
			default: null,
		},
		isHighlightTool: {
			type:    Boolean,
			default: false,
		},
		isHovered: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			imageLoadingError:  false,
			temporaryHighlight: false,
			highlightTimeout:   null,
			isInitialMount:     true,
		};
	},
	computed: {
		isCheckboxVisible() {
			return this.checkboxAlwaysVisible || this.checked;
		},
		hasContextMenu() {
			return this.menuItems?.length > 0;
		},
	},
	watch: {
		checked(value) {
			this.isChecked = value;

			// Only show the temporary highlight if it's not the initial mount
			if (!this.isInitialMount) {
				this.showTemporaryHighlight();
			}
		},
	},
	mounted() {
		// Set isInitialMount to false after a short delay to allow initial render
		this.$nextTick(() => {
			this.isInitialMount = false;
		});
	},
	beforeDestroy() {
		// Clear any remaining timeout when component is destroyed
		if (this.highlightTimeout) {
			clearTimeout(this.highlightTimeout);
		}
	},
	methods: {
		handleSelect(value) {
			this.$emit('selection-changed', { entry: this.entry, selected: value });
			this.showTemporaryHighlight();
		},
		handleRowClick(entry) {
			if (this.entry.disabled) {
				return;
			}
			if (this.isHighlightTool) {
				this.$emit('selection-changed', { entry: this.entry, selected: !this.checked });
			} else {
				this.$emit('row-click', entry);
			}
			this.showTemporaryHighlight();
		},
		showTemporaryHighlight() {
			// Clear any existing timeout to prevent multiple timeouts
			if (this.highlightTimeout) {
				clearTimeout(this.highlightTimeout);
			}

			// Show highlight immediately
			this.temporaryHighlight = true;

			// Set timeout to hide highlight with fixed 1000ms duration
			this.highlightTimeout = setTimeout(() => {
				this.temporaryHighlight = false;
			}, 300);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.tr {
	transition: background-color 0.3s ease, color 0.3s ease;

	&.disabled {
		color: var(--color-grey-700);
		@media (hover: hover)  {
			&:hover {
				cursor: initial;
			}
		}
	}

	.td.options {
		width: auto;
		flex-grow: 1;
	}

	&:hover {
		background-color: var(--one-panel-background-color-row-hover);
	}

	&.is-temporarily-selected {
		background-color: var(--color-hype-yellow);
		color: var(--color-text-on-hype-yellow);
		transition: background-color $trans-time-fast, color $trans-time-fast;
	}

	&.is-hovered {
		background-color: var(--one-panel-background-color-row-hover);
	}
}

.td {
	&.-col-checkbox {
		.tr.is-highlight-tool & {
			order: 5;
			padding: 0;
		}
	}
}

.actions-spacing {
	margin-left: 12px;
}

.row-actions {
	display: flex;
	justify-content: flex-end;
}

.checked-indicator {
	min-width: 20px;
}

</style>
